import { Pooling, PoolingMode, PoolingType } from '@resistapp/common/api-types';
import {
  SampleSubType,
  getSampleSubTypeBaseString,
  getSamplingPointSubtypePoolingKey,
  isBeforeSample,
  type SampleWithSamplingPoint,
} from '@resistapp/common/sampling-point-types';
import { getAdminAreaKey, getSamplingPointPoolingKey } from '@resistapp/common/sampling-points';
import { AdminLevelKey, BioRep, PooledSplPntHack, Sample } from '@resistapp/common/types';
import { chain, get, isNil, keys, pickBy } from 'lodash';

/**
 * Pooling samples' sampling points means rewriting their sampling points with virtual ones that are identical for the samples that are pooled together.
 *
 * Create new raw samples (with untouched abundances) that share a sampling point for all input samples that match the same pooling criteria.
 * If pooling is not specified, no pooling is performed, and samples are only mapped to new raw samples (with untouched abundances).
 */
export function poolSampleSamplingPoints<T extends Sample>(
  samples: T[],
  pooling: Pooling | undefined,
  getKey: (sample: T) => string,
): T[] {
  let mutatingSampleNumberCounter = 1;

  const pooledSamples = chain(samples)
    .filter(sample => {
      const hasKeys = hasPoolingKeys(sample, pooling);
      if (pooling?.mode === PoolingMode.THROW_MISSING && !hasKeys) {
        throw new Error(`Sample ${sample.id} is missing ${pooling.type} (${get(pooling, 'level', '')}) pooling key`);
      }
      return hasKeys;
    })
    .groupBy(sample => getKey(sample))
    .mapValues((samplesToBePooled, key, collection) => {
      const sampleNum = mutatingSampleNumberCounter++;

      // HACK! Use bogus negative ids to satisfy type system just in case some dumbass goes and upserts these in db
      // NOTE: smplPnt id should be unique across sites but identical for site samples (though they have different subtypes for building before/after datums fields)
      const samplingPointId = -1 - keys(collection).findIndex(k => k === key);
      const sampleId = -sampleNum;
      const pooledSampless = samplesToBePooled.map(sample =>
        getSampleWithPooledMetadata(
          sample,
          sampleNum,
          BioRep.A,
          sampleId,
          samplingPointId,
          samplesToBePooled,
          pooling,
          key,
        ),
      );
      return pooledSampless;
    })
    .values()
    .flatten()
    .value() as T[]; // Typescript somehow things this could be T[][]
  return pooledSamples;
}

function getSampleWithPooledMetadata<T extends Sample>(
  sample: T,
  sampleNum: number,
  bioRep: BioRep,
  sampleId: number,
  samplingPointId: number,
  samplesToBePooled: T[],
  pooling: Pooling | undefined,
  poolingKey: string,
): T {
  const keepLatLon = !pooling || pooling.type === PoolingType.SITE || pooling.type === PoolingType.SAMPLING_POINT;
  const keepCity = keepLatLon;
  const keepRegion = keepLatLon;
  const keepCountry = keepLatLon || pooling.type === PoolingType.SITE_AND_ADMIN_LEVEL;

  const adminLevels = keepLatLon
    ? sample.samplingPoint.adminLevels
    : pooling.type === PoolingType.SITE_AND_ADMIN_LEVEL
      ? pickBy(sample.samplingPoint.adminLevels, levelDatum => levelDatum.level >= pooling.level)
      : undefined;

  const inferredLat = chain(samplesToBePooled)
    .map(s => s.samplingPoint.inferredLat)
    .mean()
    .value();
  const inferredLon = chain(samplesToBePooled)
    .map(s => s.samplingPoint.inferredLon)
    .mean()
    .value();

  const uniqSubtypes = chain(samplesToBePooled)
    .map(s => s.samplingPoint.subtype)
    .uniq()
    .value();
  const numUniqSubtypes = uniqSubtypes.length;
  const isSitePooling = pooling?.type === PoolingType.SITE && numUniqSubtypes === 2;
  const keepSubtype = keepSubtypeForProcessPairs(samplesToBePooled, uniqSubtypes, isSitePooling);

  const samplingPointName = isSitePooling
    ? samplesToBePooled.find(isBeforeSample)?.samplingPoint.name || ''
    : getPoolName(sample, pooling);

  if (!samplingPointName) {
    throw new Error(`Unexpectedly falsy sampling point name for ${sample.id}`);
  }

  const pooledSample: T = {
    ...sample,
    id: sampleId,
    projects: sample.projects,
    number: sampleNum,
    bioRep,
    samplingPoint: {
      name: samplingPointName,
      type: samplesToBePooled[0].samplingPoint.type,
      subtype: keepSubtype ? sample.samplingPoint.subtype : undefined,
      id: samplingPointId,
      inferredLat,
      inferredLon,
      adminLevels,
      city: keepCity ? sample.samplingPoint.city : undefined,
      region: keepRegion ? sample.samplingPoint.region : undefined,
      country: keepCountry ? sample.samplingPoint.country : undefined,
      lat: keepLatLon ? sample.samplingPoint.lat : undefined,
      lon: keepLatLon ? sample.samplingPoint.lon : undefined,
    },
    samplingPointId,
    specificLat: keepLatLon ? sample.specificLat : undefined,
    specificLon: keepLatLon ? sample.specificLon : undefined,
  };

  // HACK, see PooledSplPntHack
  const pooledSampleSp = pooledSample.samplingPoint as PooledSplPntHack;
  pooledSampleSp.originalSamplingPointId = get(
    sample.samplingPoint,
    'originalSamplingPointId',
    sample.samplingPoint.id,
  );
  pooledSampleSp.originalSamplingPointNames = chain(samplesToBePooled)
    .map(s => get(s.samplingPoint, 'originalSamplingPointNames', [s.samplingPoint.name]))
    .flatten()
    .uniq()
    .value();
  pooledSampleSp.poolingKey = poolingKey;

  return pooledSample;
}

function keepSubtypeForProcessPairs(
  samplesToBePooled: Sample[],
  uniqSubtypes: Array<SampleSubType | null | undefined>,
  isSitePooling: boolean,
) {
  const numUniqSubtypes = uniqSubtypes.length;
  const evenNumberOfSamples = samplesToBePooled.length % 2 === 0;
  const numValidProcessPairs = chain(uniqSubtypes)
    .map(subtype => getSampleSubTypeBaseString(subtype))
    .uniq()
    .value().length;
  const numLocations = chain(samplesToBePooled)
    .map(s => `${s.samplingPoint.lat},${s.samplingPoint.lon}`)
    .uniq()
    .value().length;
  const numTimePoints = chain(samplesToBePooled)
    .map(s => {
      return typeof s.time === 'string' ? s.time : s.time?.toISOString();
    })
    .uniq()
    .value().length;
  const timePointsMatch = samplesToBePooled.length === numTimePoints * numLocations;

  // Do a rudimentary check for whether or not the area only contains process pairs
  const allowBeforeAndAfterAreaAggregation =
    evenNumberOfSamples && numUniqSubtypes / 2 === numValidProcessPairs && timePointsMatch;

  const allowedUniqueSubtypes = isSitePooling ? 2 : allowBeforeAndAfterAreaAggregation ? numUniqSubtypes : 1;
  const keepSubtype = numUniqSubtypes <= allowedUniqueSubtypes;
  return keepSubtype;
}

function hasPoolingKeys(sample: Sample, pooling: Pooling | undefined) {
  if (!pooling) {
    return true;
  }
  if (pooling.type === PoolingType.SITE_AND_ADMIN_LEVEL) {
    return !!sample.samplingPoint.adminLevels?.[`${pooling.level}` as AdminLevelKey];
  }
  if (pooling.type === PoolingType.SITE) {
    return !isNil(sample.samplingPoint.lat) && !isNil(sample.samplingPoint.lon);
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (pooling.type === PoolingType.SAMPLING_POINT) {
    return !!sample.samplingPoint.subtype;
  }
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  throw Error(`Unsupported pooling type: ${pooling.type}`);
}

export function getPoolingKey(sample: Sample, pooling: Pooling | undefined): string {
  switch (pooling?.type) {
    case PoolingType.SAMPLING_POINT:
      return getSamplingPointPoolingKey(sample);
    case PoolingType.SITE_AND_ADMIN_LEVEL: {
      const adminArea = sample.samplingPoint.adminLevels?.[`${pooling.level}` as AdminLevelKey];
      if (!adminArea) {
        throw new Error(`Sample ${sample.id} missing admin level ${pooling.level}`);
      }
      return getAdminAreaKey(adminArea);
    }
    case PoolingType.SITE:
      if (isNil(sample.samplingPoint.lat) || isNil(sample.samplingPoint.lon)) {
        throw new Error(`Sample ${sample.id} missing lat/lon`);
      }
      return getSamplingPointSubtypePoolingKey(sample as SampleWithSamplingPoint);
    case undefined: {
      const samplingPoint = sample.samplingPoint;
      if (!samplingPoint.id) {
        throw new Error(`Sample ${JSON.stringify(sample)} missing sampling point id in getPoolingKey`);
      }
      return `${samplingPoint.id}`;
    }
    default:
      throw Error(`Unexpected pooling type ${JSON.stringify(pooling)}`);
  }
}

function getPoolName(sample: Sample, pooling: Pooling | undefined): string {
  switch (pooling?.type) {
    case PoolingType.SAMPLING_POINT:
      return sample.samplingPoint.name;
    case PoolingType.SITE_AND_ADMIN_LEVEL: {
      const levelName = sample.samplingPoint.adminLevels?.[`${pooling.level}` as AdminLevelKey]?.name;
      if (!levelName) {
        // Could be due to the same commented in maxAffordableLevelByCountry ?
        throw new Error(`Sample ${sample.id} missing admin level ${pooling.level}`);
      }
      return levelName;
    }
    case PoolingType.SITE:
      return sample.samplingPoint.name;
    case undefined:
      return `${sample.samplingPoint.name} (${sample.projects[0].projectId})`;
  }
}
